import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Grid, Typography } from "@material-ui/core";
import { ValidatorForm, TextValidator } from "react-material-ui-form-validator";
import {
	Dialog,
	FormActions,
	FormButton,
	FormSubmitButton,
	Feedback,
} from "frame/components";
import { authHeaders } from "frame/auth-headers";
import { getOrganisationBaseUrlByGuid } from "frame/base-url";

const useStyles = makeStyles((theme) => ({
	body: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(2),
	},
	actions: {
		paddingBottom: theme.spacing(4),
	},
	hint: {
		color: theme.palette.text.secondary,
		// Dialog title sits 10px further in than this content area.
		marginLeft: 10,
		marginBottom: theme.spacing(2),
	},
}));

const HelpFeedbackDialog = ({
	open,
	onClose,
	organisationId,
	appVersion,
}) => {
	const classes = useStyles();
	const [message, setMessage] = useState("");
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState(false);

	useEffect(() => {
		if (open) {
			setMessage("");
			setSaving(false);
			setError("");
			setSuccess(false);
		}
	}, [open]);

	const onSubmit = async () => {
		if (saving) {
			return;
		}

		if (!organisationId) {
			setError("No organisation selected. Switch organisation and try again.");
			return;
		}

		setSaving(true);
		setError("");
		setSuccess(false);

		try {
			const response = await fetch(
				`${getOrganisationBaseUrlByGuid(organisationId)}/feedback`,
				{
					method: "POST",
					headers: authHeaders(),
					body: JSON.stringify({
						type: "Feedback",
						message: message.trim(),
						pageUrl: window.location.href,
						appVersion: appVersion || undefined,
					}),
				}
			);

			if (!response.ok) {
				let detail = "Could not send your message. Please try again.";
				try {
					const data = await response.json();
					if (data?.message) {
						detail = data.message;
					}
				} catch (_) {
					/* ignore parse errors */
				}
				setError(detail);
				setSaving(false);
				return;
			}

			setSuccess(true);
			setSaving(false);
			setMessage("");
		} catch (_) {
			setError("Could not send your message. Check your connection and try again.");
			setSaving(false);
		}
	};

	return (
		<Dialog
			title="Feedback"
			open={open}
			onClose={onClose}
			modalSize="sm"
		>
			<Typography className={classes.hint} variant="body2">
				Tell us about an issue, or make a suggestion, or give us a
				compliment.
			</Typography>

			{success ? (
				<>
					<Feedback active color="success" disableTimeout>
						Thanks, your message has been sent.
					</Feedback>
					<FormActions className={classes.actions}>
						<FormButton onClick={onClose}>Close</FormButton>
					</FormActions>
				</>
			) : (
				<ValidatorForm
					autoComplete="off"
					noValidate
					onSubmit={onSubmit}
				>
					<Grid container spacing={2} className={classes.body}>
						<Grid item xs={12}>
							<TextValidator
								fullWidth
								multiline
								rows={4}
								variant="outlined"
								label="Message"
								name="message"
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								validators={["required"]}
								errorMessages={["Please enter a short message"]}
								disabled={saving}
							/>
						</Grid>
						{error && (
							<Grid item xs={12}>
								<Feedback active color="error" disableTimeout>
									{error}
								</Feedback>
							</Grid>
						)}
					</Grid>
					<FormActions className={classes.actions}>
						<FormSubmitButton disabled={saving} loading={saving}>
							Send
						</FormSubmitButton>
						<FormButton disabled={saving} onClick={onClose}>
							Cancel
						</FormButton>
					</FormActions>
				</ValidatorForm>
			)}
		</Dialog>
	);
};

HelpFeedbackDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	organisationId: PropTypes.string,
	appVersion: PropTypes.string,
};

export default HelpFeedbackDialog;
